import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  endpoints,
  type MarketLedgerEntry,
  type MarketManagement,
  type MarketOrder,
  type MarketSource,
} from '../api';
import { Button, EmptyState, ErrorState, Field, LoadingState, Note, Page, Section, Stat, Stats, Status, currency, dateTime, number } from '../components';

const POLL_INTERVAL_MS = 30_000;
const LEDGER_PAGE_SIZE = 25;

type Tab = 'active' | 'history' | 'ledger';
type Notice = { tone: 'info' | 'success' | 'error'; text: string };

export function MarketManagementPage() {
  const [data, setData] = useState<MarketManagement | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [tab, setTab] = useState<Tab>('active');
  const [orderSearch, setOrderSearch] = useState('');
  const [orderSide, setOrderSide] = useState<'all' | 'sell' | 'buy'>('all');
  const [orderState, setOrderState] = useState('all');
  const [ledgerSearch, setLedgerSearch] = useState('');
  const [ledgerType, setLedgerType] = useState<'all' | MarketLedgerEntry['type']>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [ledgerPage, setLedgerPage] = useState(1);
  const mounted = useRef(false);
  const requestInFlight = useRef(false);

  const load = useCallback(async (visible = false) => {
    if (requestInFlight.current) return;
    requestInFlight.current = true;
    if (visible && mounted.current) setRefreshing(true);
    try {
      const result = await endpoints.marketManagement();
      if (!mounted.current) return;
      setData(result);
      setError(null);
    } catch (caught) {
      if (mounted.current) setError(caught instanceof Error ? caught : new Error('No se pudo consultar el mercado'));
    } finally {
      requestInFlight.current = false;
      if (mounted.current) {
        setLoading(false);
        if (visible) setRefreshing(false);
      }
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    void load();
    const timer = window.setInterval(() => void load(), POLL_INTERVAL_MS);
    return () => {
      mounted.current = false;
      window.clearInterval(timer);
    };
  }, [load]);

  const activeOrders = useMemo(() => (data?.orders ?? []).filter(isActiveOrder), [data]);
  const historicalOrders = useMemo(() => (data?.orders ?? []).filter((order) => !isActiveOrder(order)), [data]);
  const visibleOrders = useMemo(() => {
    const source = tab === 'history' ? historicalOrders : activeOrders;
    const query = normalize(orderSearch);
    return source.filter((order) => {
      const searchable = normalize([order.typeName, order.typeId, order.locationName, order.locationId, order.id].join(' '));
      return (!query || searchable.includes(query))
        && (orderSide === 'all' || order.side === orderSide)
        && (orderState === 'all' || order.state === orderState);
    }).sort((left, right) => Date.parse(right.issuedAt) - Date.parse(left.issuedAt));
  }, [activeOrders, historicalOrders, orderSearch, orderSide, orderState, tab]);

  const availableStates = useMemo(() => {
    const source = tab === 'history' ? historicalOrders : activeOrders;
    return [...new Set(source.map((order) => order.state))].sort((left, right) => left.localeCompare(right, 'es'));
  }, [activeOrders, historicalOrders, tab]);

  const filteredLedger = useMemo(() => {
    const query = normalize(ledgerSearch);
    return (data?.ledger ?? []).filter((entry) => {
      const searchable = normalize([
        entry.typeName, entry.typeId, entry.locationName, entry.locationId, entry.id,
        entry.sourceId, entry.journalRefId, entry.description, entry.refType, entry.counterpartyId,
      ].join(' '));
      const day = localDateKey(entry.date);
      return (!query || searchable.includes(query))
        && (ledgerType === 'all' || entry.type === ledgerType)
        && (!dateFrom || day >= dateFrom)
        && (!dateTo || day <= dateTo);
    }).sort((left, right) => Date.parse(right.date) - Date.parse(left.date));
  }, [data, dateFrom, dateTo, ledgerSearch, ledgerType]);

  useEffect(() => setLedgerPage(1), [ledgerSearch, ledgerType, dateFrom, dateTo]);
  useEffect(() => setOrderState('all'), [tab]);

  const ledgerPages = Math.max(1, Math.ceil(filteredLedger.length / LEDGER_PAGE_SIZE));
  const safeLedgerPage = Math.min(ledgerPage, ledgerPages);
  const ledgerRows = filteredLedger.slice((safeLedgerPage - 1) * LEDGER_PAGE_SIZE, safeLedgerPage * LEDGER_PAGE_SIZE);
  const missingScope = data?.sources.some((source) => source.status === 'missing_scope') ?? false;
  const currentSourceCount = data?.sources.filter((source) => source.status === 'current').length ?? 0;
  const nonCurrentSourceCount = (data?.sources.length ?? 0) - currentSourceCount;

  async function requestSync() {
    setSyncing(true);
    setNotice(null);
    try {
      const result = await endpoints.startSync('character');
      if (!mounted.current) return;
      setNotice({ tone: 'info', text: `Sincronización del piloto encolada. Se respetará la caché de ESI; los datos aparecerán cuando el proceso termine. ${result.detail}` });
      await load(true);
    } catch (caught) {
      if (mounted.current) setNotice({ tone: 'error', text: caught instanceof Error ? caught.message : 'No se pudo encolar la sincronización' });
    } finally {
      if (mounted.current) setSyncing(false);
    }
  }

  return <Page title="Órdenes" actions={<>
    <a className="button button--ghost" href="/api/documentation/market" target="_blank" rel="noreferrer">Guía ↗</a>
    <Button variant="ghost" disabled={refreshing} onClick={() => void load(true)}>{refreshing ? 'Actualizando…' : 'Actualizar'}</Button>
    <Button variant="secondary" disabled={syncing || refreshing} onClick={() => void requestSync()}>{syncing ? 'Encolando…' : 'Sincronizar piloto'}</Button>
  </>}>
    {notice && <Note tone={notice.tone}>{notice.text}</Note>}
    {loading && !data && <LoadingState />}
    {error && !data && <ErrorState error={error} onRetry={() => void load(true)} />}
    {error && data && <Note tone="error">No se pudo actualizar. Se conservan los últimos datos: {error.message}</Note>}
    {missingScope && <Note tone="error">Faltan permisos ESI para alguna fuente. <a className="link" href="#/sync">Revisa la conexión</a> y vuelve a sincronizar.</Note>}

    {data && <>
      <Stats label="Resumen global capturado">
        <Stat label="Ventas activas" value={number(data.summary.activeSellOrders)} detail={`${money(data.summary.sellRemainingValue)} pendientes`} />
        <Stat label="Compras activas" value={number(data.summary.activeBuyOrders)} detail={`${money(data.summary.buyEscrow)} en depósito`} />
        <Stat label="Ventas brutas" value={currency(data.summary.salesGross)} detail="Todo el historial" />
        <Stat label="Compras brutas" value={currency(data.summary.purchasesGross)} detail="Todo el historial" />
        <Stat label="Costes de mercado" value={currency(data.summary.fees)} detail="Tasas e intermediación" />
        <Stat label="Flujo del monedero" value={`${data.summary.netCashflow > 0 ? '+' : ''}${currency(data.summary.netCashflow)}`} detail="Incluye depósitos y devoluciones" tone={data.summary.netCashflow >= 0 ? 'pos' : 'neg'} />
      </Stats>

      <Section>
        <div className="tabs" role="tablist" aria-label="Datos de mercado">
          <TabButton active={tab === 'active'} onClick={() => setTab('active')}>Activas<span className="count">{activeOrders.length}</span></TabButton>
          <TabButton active={tab === 'history'} onClick={() => setTab('history')}>Historial<span className="count">{historicalOrders.length}</span></TabButton>
          <TabButton active={tab === 'ledger'} onClick={() => setTab('ledger')}>Operaciones<span className="count">{data.ledger.length}</span></TabButton>
        </div>
        {tab === 'ledger'
          ? <LedgerView entries={filteredLedger} rows={ledgerRows} search={ledgerSearch} setSearch={setLedgerSearch} type={ledgerType} setType={setLedgerType} dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo} page={safeLedgerPage} pages={ledgerPages} setPage={setLedgerPage} />
          : <OrdersView orders={visibleOrders} search={orderSearch} setSearch={setOrderSearch} side={orderSide} setSide={setOrderSide} state={orderState} setState={setOrderState} states={availableStates} history={tab === 'history'} />}
      </Section>

      <details>
        <summary>Procedencia de los datos · {currentSourceCount} {currentSourceCount === 1 ? 'fuente vigente' : 'fuentes vigentes'}{nonCurrentSourceCount > 0 && <span className="warn"> · {nonCurrentSourceCount} requiere{nonCurrentSourceCount === 1 ? '' : 'n'} atención</span>}</summary>
        <div className="stack">
          <p className="fine">Solo lectura: las órdenes se gestionan en el cliente de EVE. El resumen usa todo el historial capturado de la wallet personal y no cambia con los filtros. El flujo procede del journal ESI; el libro omite sus apuntes <code>market_transaction</code> para no duplicar compras y ventas, y muestra el escrow aparte. {data.coverage.notice}</p>
          <dl className="meta">
            <div><dt>Primer apunte</dt><dd>{dateTime(data.coverage.firstCapturedAt)}</dd></div>
            <div><dt>Último apunte</dt><dd>{dateTime(data.coverage.lastCapturedAt)}</dd></div>
            <div><dt>Transacciones</dt><dd>{number(data.coverage.transactionCount)}</dd></div>
            <div><dt>Apuntes de diario</dt><dd>{number(data.coverage.journalCount)}</dd></div>
          </dl>
          <div className="table-wrap"><table><tbody>{data.sources.map((source) => <SourceRow key={source.module} source={source} />)}</tbody></table></div>
        </div>
      </details>
    </>}
  </Page>;
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return <button type="button" role="tab" aria-selected={active} onClick={onClick}>{children}</button>;
}

function SourceRow({ source }: { source: MarketSource }) {
  return <tr>
    <td><strong>{source.label}</strong></td>
    <td><Status value={source.status} label={sourceStatus(source.status)} /></td>
    <td className="muted">{source.detail}</td>
    <td className="muted" style={{ whiteSpace: 'nowrap' }}>{source.lastSyncAt ? dateTime(source.lastSyncAt) : 'Sin sincronizar'}</td>
  </tr>;
}

function OrdersView({ orders, search, setSearch, side, setSide, state, setState, states, history }: {
  orders: MarketOrder[];
  search: string;
  setSearch: (value: string) => void;
  side: 'all' | 'sell' | 'buy';
  setSide: (value: 'all' | 'sell' | 'buy') => void;
  state: string;
  setState: (value: string) => void;
  states: string[];
  history: boolean;
}) {
  const remaining = orders.reduce((sum, order) => sum + order.remainingValue, 0);
  const filled = orders.reduce((sum, order) => sum + order.filledVolume, 0);
  const hasUnknownState = history && orders.some((order) => order.state.toLowerCase() === 'unknown');
  return <div className="stack" role="tabpanel">
    <div className="filters">
      <Field label="Buscar órdenes" wide><input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Artículo, ubicación o ID" /></Field>
      <Field label="Tipo"><select value={side} onChange={(event) => setSide(event.target.value as 'all' | 'sell' | 'buy')}><option value="all">Compras y ventas</option><option value="sell">Ventas</option><option value="buy">Compras</option></select></Field>
      <Field label="Estado"><select value={state} onChange={(event) => setState(event.target.value)}><option value="all">Todos</option>{states.map((value) => <option value={value} key={value}>{value}</option>)}</select></Field>
    </div>
    <p className="fine">{number(orders.length)} órdenes · {number(filled)} unidades ejecutadas · {money(remaining)} {history ? 'de valor residual' : 'pendientes'}</p>
    {hasUnknownState && <Note>«Estado desconocido» significa que la orden ya no aparece en ESI; no demuestra que se haya vendido.</Note>}
    {orders.length === 0
      ? <EmptyState title={history ? 'Sin órdenes históricas con estos filtros' : 'Sin órdenes activas con estos filtros'} />
      : <div className="table-wrap"><table className="table--stack"><thead><tr><th>Artículo</th><th>Tipo</th><th>Estado</th><th className="num">Precio</th><th className="num">Volumen</th><th className="num">Pendiente</th><th>Ubicación</th><th>Vence</th></tr></thead><tbody>{orders.map((order) => <tr key={order.id}>
        <td className="wide"><strong>{order.typeName}</strong><small className="mono">Orden {order.id}</small></td>
        <td data-label="Tipo">{order.side === 'sell' ? 'Venta' : 'Compra'}{order.walletDivision !== null && <small>División {order.walletDivision}</small>}</td>
        <td data-label="Estado"><Status value={order.state.toLowerCase() === 'open' ? 'active' : order.state} /></td>
        <td className="num" data-label="Precio">{money(order.price)}</td>
          <td className="num wide" data-label="Volumen"><OrderVolume order={order} /></td>
        <td className="num" data-label="Pendiente">{money(order.remainingValue)}{order.escrow > 0 && <small>Depósito {money(order.escrow)}</small>}</td>
        <td className="wide" data-label="Ubicación">{order.locationName}</td>
        <td className="wide muted" data-label="Vence">{order.expiresAt ? dateTime(order.expiresAt) : 'Sin informar'}<small>Emitida {dateTime(order.issuedAt)}</small></td>
      </tr>)}</tbody></table></div>}
  </div>;
}

function OrderVolume({ order }: { order: MarketOrder }) {
  const total = Math.max(0, order.volumeTotal);
  const executed = Math.min(total, Math.max(0, order.filledVolume));
  const percent = total > 0 ? executed / total * 100 : 0;
  const description = `${number(executed)} de ${number(total)} unidades ejecutadas · ${number(order.volumeRemain)} pendientes`;
  return <div className="order-volume" role="progressbar" aria-label={`Volumen ejecutado de ${order.typeName}`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={percent} aria-valuetext={description} title={description}>
    <span className="order-volume-fill" style={{ width: `${percent}%` }} aria-hidden="true" />
    <span className="order-volume-value">{number(executed)} / {number(total)}</span>
  </div>;
}

function LedgerView({ entries, rows, search, setSearch, type, setType, dateFrom, setDateFrom, dateTo, setDateTo, page, pages, setPage }: {
  entries: MarketLedgerEntry[];
  rows: MarketLedgerEntry[];
  search: string;
  setSearch: (value: string) => void;
  type: 'all' | MarketLedgerEntry['type'];
  setType: (value: 'all' | MarketLedgerEntry['type']) => void;
  dateFrom: string;
  setDateFrom: (value: string) => void;
  dateTo: string;
  setDateTo: (value: string) => void;
  page: number;
  pages: number;
  setPage: (value: number | ((current: number) => number)) => void;
}) {
  const sales = entries.filter((entry) => entry.type === 'sale').reduce((sum, entry) => sum + entry.amount, 0);
  const purchases = entries.filter((entry) => entry.type === 'purchase').reduce((sum, entry) => sum + entry.amount, 0);
  const fees = entries.filter((entry) => entry.type === 'fee').reduce((sum, entry) => sum + entry.amount, 0);
  return <div className="stack" role="tabpanel">
    <div className="filters">
      <Field label="Buscar operaciones" wide><input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Artículo, ubicación, descripción o ID" /></Field>
      <Field label="Tipo"><select value={type} onChange={(event) => setType(event.target.value as typeof type)}><option value="all">Todos</option><option value="sale">Venta</option><option value="purchase">Compra</option><option value="fee">Coste</option><option value="adjustment">Ajuste</option></select></Field>
      <Field label="Desde"><input type="date" value={dateFrom} max={dateTo || undefined} onChange={(event) => setDateFrom(event.target.value)} /></Field>
      <Field label="Hasta"><input type="date" value={dateTo} min={dateFrom || undefined} onChange={(event) => setDateTo(event.target.value)} /></Field>
      <Button variant="secondary" disabled={entries.length === 0} onClick={() => exportLedger(entries)}>Exportar CSV</Button>
    </div>
    <p className="fine">{number(entries.length)} apuntes · ventas {signedMoney(sales)} · compras {signedMoney(purchases)} · costes {signedMoney(fees)}</p>
    {entries.length === 0
      ? <EmptyState title="Sin operaciones con estos filtros" />
      : <><div className="table-wrap"><table className="table--stack"><thead><tr><th>Fecha</th><th>Tipo</th><th>Artículo</th><th className="num">Cantidad</th><th className="num">Precio</th><th className="num">Importe</th><th>Ubicación</th><th>Referencias</th></tr></thead><tbody>{rows.map((entry) => <tr key={entry.id}>
        <td style={{ whiteSpace: 'nowrap' }} className="muted" data-label="Fecha">{dateTime(entry.date)}</td>
        <td data-label="Tipo">{ledgerTypeLabel(entry.type)}</td>
        <td className="wide"><strong>{entry.typeName ?? (entry.refType === 'market_escrow' ? 'Movimiento de escrow' : entry.description)}</strong>{entry.typeName && <small>{entry.description}</small>}</td>
        <td className="num" data-label="Cantidad">{number(entry.quantity)}</td>
        <td className="num" data-label="Precio">{entry.unitPrice === null ? '—' : money(entry.unitPrice)}</td>
        <td className={`num ${entry.amount >= 0 ? 'pos' : 'neg'}`} data-label="Importe">{signedMoney(entry.amount)}{entry.balance !== null && <small className="muted">Saldo {money(entry.balance)}</small>}</td>
        <td data-label="Ubicación">{entry.locationName ?? '—'}</td>
        <td className="wide mono muted"><small>{entry.source === 'transaction' ? 'Transacción' : 'Diario'} {entry.sourceId}{entry.journalRefId ? ` · Diario ${entry.journalRefId}` : entry.refType ? ` · ${entry.refType}` : ''}{entry.counterpartyId ? ` · Contraparte ${entry.counterpartyId}` : ''}</small></td>
      </tr>)}</tbody></table></div>
      <div className="pager"><span>Página {page} de {pages} · {entries.length} resultados</span><div><Button variant="ghost" disabled={page <= 1} onClick={() => setPage((current) => Math.max(1, current - 1))}>Anterior</Button><Button variant="ghost" disabled={page >= pages} onClick={() => setPage((current) => Math.min(pages, current + 1))}>Siguiente</Button></div></div></>}
  </div>;
}

function isActiveOrder(order: MarketOrder) {
  return ['open', 'active'].includes(order.state.toLowerCase());
}

function normalize(value: unknown) {
  return String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
}

function localDateKey(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value.slice(0, 10);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function money(value: number) {
  return `${new Intl.NumberFormat('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: 'always' }).format(value)} ISK`;
}

function signedMoney(value: number) {
  return `${value > 0 ? '+' : ''}${money(value)}`;
}

function sourceStatus(status: MarketSource['status']) {
  return { current: 'Vigente', stale: 'Caducada', missing_scope: 'Falta permiso', pending: 'Pendiente', error: 'Error' }[status];
}

function ledgerTypeLabel(type: MarketLedgerEntry['type']) {
  return { sale: 'Venta', purchase: 'Compra', fee: 'Coste', adjustment: 'Ajuste' }[type];
}

function exportLedger(entries: MarketLedgerEntry[]) {
  const header = ['Fecha', 'Tipo', 'Origen', 'ID origen', 'Artículo', 'ID tipo', 'Cantidad', 'Precio unitario ISK', 'Importe ISK', 'Ubicación', 'ID ubicación', 'Tipo referencia', 'Descripción', 'ID diario', 'Saldo ISK', 'ID contraparte'];
  const rows = entries.map((entry) => [
    entry.date, ledgerTypeLabel(entry.type), entry.source, entry.sourceId, entry.typeName, entry.typeId,
    entry.quantity, entry.unitPrice, entry.amount, entry.locationName, entry.locationId, entry.refType,
    entry.description, entry.journalRefId, entry.balance, entry.counterpartyId,
  ]);
  const csv = [header, ...rows].map((row) => row.map(csvCell).join(',')).join('\r\n');
  const url = URL.createObjectURL(new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = `libro-mercado-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function csvCell(value: string | number | null) {
  let text = value === null ? '' : String(value);
  if (typeof value === 'string' && (/^\s*[=+\-@]/.test(text) || /^[\t\r\n]/.test(text))) text = `'${text}`;
  return `"${text.replaceAll('"', '""')}"`;
}
